import React from "react";
import { DownlaodNew } from "../../../../IconComponents";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import LMSButton from "../../../../../Pages/DMS/Common/LMSButton/LMSButton";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

const ExportTableData = ({
  data,
  columns,
  filename,
  useLMSButton,
  isExportDisabled,
  setIsExportDisabled,
  selectedView,
  districtData,
  selectedFromDate,
  selectedToDate,
  humanLoss = false,
  fireReport = false,
  coldWave = false,
  heatwaveReport = false,
}) => {
  const handleDownload = async () => {
    setIsExportDisabled(false);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");

    function formatDate(date) {
      const formattedDate = new Date(date);
      const day = String(formattedDate.getDate()).padStart(2, "0");
      const month = String(formattedDate.getMonth() + 1).padStart(2, "0");
      const year = formattedDate.getFullYear();
      return `${day}-${month}-${year}`;
    }

    const reportName = humanLoss
      ? "Human Loss MIS Report"
      : fireReport
      ? `${selectedView.toUpperCase()}_FIRE_REPORT`
      : coldWave
      ? `${selectedView.toUpperCase()}_COLD_WAVE_REPORT`
      : heatwaveReport
      ? `${selectedView.toUpperCase()}_HEAT_WAVE_REPORT`
      : `${selectedView.toUpperCase()}_REPORT`;

    worksheet.addRow([`Report Name: ${reportName}`]);

    worksheet.addRow([`From Date: ${formatDate(selectedFromDate)}`]);
    worksheet.addRow([`To Date: ${formatDate(selectedToDate)}`]);

    worksheet.addRow(columns.map((col) => col.label));

    data.forEach((row) => {
      const rowData = columns.map((col) => row[col.label]);
      worksheet.addRow(rowData);
    });

    worksheet.mergeCells(1, 1, 1, columns.length);
    worksheet.getCell("A1").alignment = {
      horizontal: "left",
      vertical: "middle",
    };
    worksheet.getCell("A1").font = { bold: true };

    worksheet.mergeCells(2, 1, 2, columns.length);
    worksheet.getCell("A2").alignment = {
      horizontal: "left",
      vertical: "middle",
    };
    worksheet.getCell("A2").font = { bold: true };

    worksheet.mergeCells(3, 1, 3, columns.length);
    worksheet.getCell("A3").alignment = {
      horizontal: "left",
      vertical: "middle",
    };
    worksheet.getCell("A3").font = { bold: true };

    worksheet.getRow(4).eachCell((cell) => {
      cell.alignment = { horizontal: "left", vertical: "middle" }; // Always center-align
      cell.font = { bold: true };
    });

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 4) {
        row.eachCell((cell) => {
          cell.alignment = { horizontal: "left", vertical: "middle" }; // Always center-align
        });
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `${filename}.xlsx`);
    setIsExportDisabled(true);
  };

  const handleIconDownload = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");

    if (filename === "District wise ATMs") {
      // Get ATM data from session storage
      const storedData = JSON.parse(
        sessionStorage.getItem("districtWiseATMs") || "[]"
      );

      // Define columns for ATM data
      const atmColumns = [
        { header: "District Name", key: "districtName", width: 20 },
        { header: "ATM Name", key: "name", width: 20 },
        { header: "ATM Code", key: "code", width: 15 },
        { header: "Address", key: "addressLine", width: 30 },
      ];

      worksheet.columns = atmColumns;

      // Set the header row to bold
      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).alignment = {
        horizontal: "left",
        vertical: "middle",
      };

      // Process the data - important! We're using atmDetails instead of branchDetails
      let rowNumber = 2; // Start from row 2 after headers
      storedData.forEach((district) => {
        if (district.atmDetails && district.atmDetails.length > 0) {
          district.atmDetails.forEach((atm) => {
            const address = [atm.addressLine, atm.city, atm.mandal, atm.pinCode]
              .filter(Boolean)
              .join(", ");
            // Only include active ATMs
            if (atm.is_deleted === false) {
              worksheet.addRow({
                districtName: district.districtName,
                name: atm.name,
                code: atm.code || "N/A",
                addressLine: address || "No address available",
              });

              // Format the row
              const row = worksheet.getRow(rowNumber);
              row.alignment = { horizontal: "left", vertical: "middle" };
              rowNumber++;
            }
          });
        }
      });

      // Adjust the column widths to fit content
      worksheet.columns.forEach((column) => {
        column.width = column.width || 15;
      });
    }

    // Handle DistrictWiseBranches special case from HEAD version
    if (filename === "DistrictWiseBranches") {
      const storedData = JSON.parse(
        sessionStorage.getItem("districtWiseBranches") || "[]"
      );

      const branchColumns = [
        { header: "District Name", key: "districtName", width: 20 },
        { header: "Branch Name", key: "branchName", width: 20 },
        { header: "IFSC Code", key: "ifscCode", width: 15 },
        { header: "Branch Address", key: "branchAddress", width: 30 },
      ];

      worksheet.columns = branchColumns;

      let rowData = [];
      storedData.forEach((district) => {
        if (district.branchDetails && district.branchDetails.length > 0) {
          district.branchDetails.forEach((branch) => {
            // if (branch.activeStatus === true) {
            // Format address
            const address = [
              branch.addressLine1,
              branch.addressLine2,
              branch.city,
              branch.mandal,
              branch.pinCode,
            ]
              .filter(Boolean)
              .join(", ");

            rowData.push({
              districtName: district.districtName,
              branchName: branch.branchName,
              ifscCode: branch.ifscCode || "N/A",
              branchAddress: address || "No address available",
            });
            // }
          });
        }
      });

      rowData.forEach((row) => {
        worksheet.addRow(row);
      });

      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).alignment = {
        horizontal: "left",
        vertical: "middle",
      };
    } else {
      // Style configurations from the other version
      const bankStyle = {
        font: { bold: true, color: { argb: "FF000000" } },
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFCCFFCC" },
        }, // Light green
      };

      const branchStyle = {
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF2F2F2" },
        }, // Light gray
      };

      const contactStyle = {
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFEEEEFF" },
        }, // Light blue
      };

      // Add column headers
      worksheet.columns = columns.map((col) => ({
        header: col.label,
        key: col.label,
        width: 20,
      }));

      // Format header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF4899B7" }, // Header color
      };
      headerRow.alignment = { horizontal: "left", vertical: "middle" };

      // Add data rows with hierarchical styling
      data.forEach((row, index) => {
        const excelRow = worksheet.addRow(columns.map((col) => row[col.label]));

        // Apply styling based on record type
        if (row["Record Type"] === "Bank") {
          excelRow.eachCell({ includeEmpty: true }, (cell) => {
            cell.font = bankStyle.font;
            cell.fill = bankStyle.fill;
          });
        } else if (row["Record Type"] === "Branch") {
          excelRow.eachCell({ includeEmpty: true }, (cell) => {
            cell.fill = branchStyle.fill;
          });
          // Indent branch rows for visual hierarchy
          worksheet.getCell(`A${index + 2}`).alignment = {
            horizontal: "left",
            indent: 1,
          };
        } else if (row["Record Type"] === "Contact") {
          excelRow.eachCell({ includeEmpty: true }, (cell) => {
            cell.fill = contactStyle.fill;
          });
          // Indent contact rows further
          worksheet.getCell(`A${index + 2}`).alignment = {
            horizontal: "left",
            indent: 2,
          };
        }
      });

      // Format all data cells
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          // Skip header row
          row.eachCell((cell) => {
            cell.alignment = {
              horizontal: "left",
              vertical: "middle",
              wrapText: true,
            };
          });
        }
      });

      // Special formatting for specific file types (from HEAD version)
      if (filename.startsWith("RoleTable")) {
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber !== 1) {
            // Skip the header row
            const createdOnCell = row.getCell("Created On");
            // Handle "Created On" column: Convert timestamp to readable date
            if (createdOnCell.value) {
              const timestamp = parseFloat(createdOnCell.value);
              const date = new Date(timestamp);
              createdOnCell.value = date.toLocaleString();
            }
          }
        });
      }

      if (filename.startsWith("DailyFloodReportStatus")) {
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber !== 1) {
            // Skip header row
            const shouldHighlightRow =
              row.getCell("Affected Blocks").value === 0 &&
              row.getCell("Partially AffectedBlocks").value === 0 &&
              row.getCell("NotAffected Blocks").value === 0;

            const areBlocksEqual =
              row.getCell("Total Blocks").value ===
              row.getCell("TotalMarked Blocks").value;

            row.eachCell({ includeEmpty: true }, (cell) => {
              if (shouldHighlightRow) {
                cell.fill = {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FFFF0000" }, // Red
                };
              } else if (areBlocksEqual) {
                cell.fill = {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FF00FF00" }, // Light green
                };
              } else {
                cell.fill = {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FFFFFF00" }, // Yellow
                };
              }
            });
          }
        });
      }
    }

    // Generate the Excel file and trigger download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const now = new Date();
    now.setMinutes(now.getMinutes() + 330); // Convert UTC to IST (+5:30)

    const istDate = now.toISOString().split("T")[0]; // YYYY-MM-DD
    const istTime = now
      .toISOString()
      .split("T")[1]
      .split(".")[0]
      .replace(/:/g, ""); // HHMMSS

    const timestamp = `${istDate}_${istTime}`;
    saveAs(blob, `${filename}_${timestamp}.xlsx`);
  };

  const exportTooltip = (
    <Tooltip id="export-tooltip">Export will be enabled once saved</Tooltip>
  );

  return (
    <>
      {useLMSButton ? (
        isExportDisabled ? (
          <OverlayTrigger placement="top" overlay={exportTooltip}>
            <span>
              <LMSButton
                size="btn_sm"
                label="Export"
                onClick={handleDownload}
                variant="primary"
                disabled={isExportDisabled}
                style={{
                  opacity: isExportDisabled ? 0.2 : 1,
                  transition: "opacity 0.3s ease",
                }}
              />
            </span>
          </OverlayTrigger>
        ) : (
          <LMSButton
            size="btn_sm"
            label="Export"
            onClick={handleDownload}
            variant="primary"
            disabled={isExportDisabled}
            style={{
              opacity: isExportDisabled ? 0.2 : 1,
              transition: "opacity 0.3s ease",
            }}
          />
        )
      ) : (
        <div style={{ margin: "0.3em" }}>
          <button
            onClick={handleIconDownload}
            style={{
              background: "none",
              border: "1px solid #D0D4DC",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              padding: "6px 10px",
              cursor: "pointer",
              gap: "8px",
            }}
          >
            <DownlaodNew color="#17181a" width="1.2em" height="1.2em" />
            <span>Download</span>
          </button>
        </div>
      )}
    </>
  );
};

export default ExportTableData;
